import { v4 as uuidv4 } from 'uuid';

import { supabase } from './supabaseClient';
import { insertUnitsForCourse } from './unitsService';
import { forkVideosForCourse } from './videoLibraryService';
import { forkProjectsForCourse } from './projectService';

const courses_table_name = 'ml_courses_staging';
const units_table_name = 'ml_units_staging';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeUnitPosition = (unit, fallbackIndex = 0) => {
  const parsed = Number(unit?.position ?? unit?.unit_index ?? fallbackIndex);
  return Number.isFinite(parsed) ? parsed : fallbackIndex;
};

const normalizeCourseUnits = (units) =>
  (Array.isArray(units) ? units : [])
    .map((unit, index) => ({
      ...unit,
      position: normalizeUnitPosition(unit, index),
      currentPageIndex: unit?.current_page_index ?? unit?.currentPageIndex ?? 0,
    }))
    .sort((a, b) => {
      const byPosition = normalizeUnitPosition(a) - normalizeUnitPosition(b);
      if (byPosition !== 0) return byPosition;
      return String(a?.title || a?.unit_title || '').localeCompare(
        String(b?.title || b?.unit_title || '')
      );
    });

/**
 * Save a generated course and its units to Supabase
 * @param {Object} courseData - must include `.units` and `.id`
 */
export const saveCourseWithUnits = async (courseData) => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const toTextArray = (value) => {
    if (Array.isArray(value)) return value.map((x) => String(x).trim()).filter(Boolean);
    if (typeof value === 'string') {
      return value
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
    }
    return [];
  };
  const parsedLessons = Number.parseInt(courseData.lessons, 10);
  const parsedWeeklyHours = Number.parseInt(courseData.weeklyHours || courseData.weekly_hours, 10);
  const courseId = UUID_RE.test(String(courseData.id || '')) ? String(courseData.id) : uuidv4();

  const course = {
    id: courseId,
    title: courseData.title,
    description: courseData.description,
    objectives: toTextArray(courseData.objectives),
    prior_knowledge: courseData.priorKnowledge || courseData.prior_knowledge,
    difficulty_level: courseData.difficultyLevel || courseData.difficulty_level,
    weekly_hours: Number.isFinite(parsedWeeklyHours) ? parsedWeeklyHours : null,
    reference_books:
      courseData.referenceBooks || courseData.ReferenceBooks || courseData.reference_books,
    lessons: Number.isFinite(parsedLessons) ? parsedLessons : 0,
    current_lesson_index: courseData.current_lesson_index ?? 0,
    progress: courseData.progress ?? 0,
    creator_id: courseData.creator_id || session.user.id,
    owner_id: courseData.owner_id || session.user.id,
    fork_level: courseData.fork_level ?? 0,
    created_at: new Date().toISOString(),
  };

  const { data: courseSaved, error: courseError } = await supabase
    .from(courses_table_name)
    .insert([course])
    .select()
    .single();

  if (courseError) {
    return { error: courseError };
  }

  const units = courseData.units || [];

  // eslint-disable-next-line no-console
  console.log('[saveCourseWithUnits] Target course_id for units insert:', course.id);
  // eslint-disable-next-line no-console
  console.log('[saveCourseWithUnits] Prepared units payload before insert:', units);

  const { data: insertedUnits, error: unitsError } = await insertUnitsForCourse(course.id, units);

  if (unitsError) {
    return { error: unitsError };
  }

  // eslint-disable-next-line no-console
  console.log('[saveCourseWithUnits] insertedUnits from DB:', insertedUnits);

  const fullCourse = {
    ...courseSaved,
    difficultyLevel: courseSaved.difficulty_level || courseData.difficultyLevel || 'Beginner',
    units: normalizeCourseUnits(insertedUnits).map((u) => ({
      title: u.title,
      unit_index: u.position,
      objective: u.unit_objective,
      assessment: u.unit_assessment,
      position: u.position,
      id: u.id,
      course_id: u.course_id,
    })),
  };

  // eslint-disable-next-line no-console
  console.log('[saveCourseWithUnits] fullCourse with mapped units:', fullCourse);

  const existingCourses = JSON.parse(localStorage.getItem('courses')) || [];
  const existingIndex = existingCourses.findIndex((c) => c.id === fullCourse.id);
  if (existingIndex >= 0) {
    const existing = existingCourses[existingIndex];
    existingCourses[existingIndex] = {
      ...existing,
      ...fullCourse,
      units:
        Array.isArray(fullCourse.units) && fullCourse.units.length > 0
          ? fullCourse.units
          : existing.units || [],
    };
    localStorage.setItem('courses', JSON.stringify(existingCourses));
  } else {
    localStorage.setItem('courses', JSON.stringify([...existingCourses, fullCourse]));
  }

  return { data: fullCourse };
};

export const forkCourseWithUnits = async (originalCourse) => {
  // Validate original course has an ID
  if (!originalCourse.id) {
    return { error: 'Original course must have an ID to fork videos and projects' };
  }

  // 🔐 Get current authenticated user
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { error: sessionError || 'No authenticated session.' };
  }

  const currentUserId = session.user.id;

  // 🆕 New course ID
  const newCourseId = uuidv4();

  // 🧬 Prepare forked course

  const forkedCourseData = {
    id: newCourseId,
    title: originalCourse.title,
    description: originalCourse.description,
    objectives: originalCourse.objectives,
    prior_knowledge: originalCourse.prior_knowledge,
    difficulty_level: originalCourse.difficulty_level,
    weekly_hours: originalCourse.weekly_hours,
    reference_books: originalCourse.reference_books,
    units: (originalCourse.units || []).map((unit, i) => ({
      title: unit.title || unit.lessonTitle,
      objective: unit.unit_objective || unit.objective || '',
      assessment: unit.unit_assessment || unit.assessment || '',
      position: unit.position ?? unit.unit_index ?? i,
    })),
    // 👇 Additional metadata handled inside saveCourseWithUnits
    creator_id: originalCourse.creator_id || originalCourse.owner_id,
    owner_id: currentUserId,
    fork_level: (originalCourse.fork_level ?? 0) + 1,
    current_lesson_index: 0,
    progress: 0,
  };

  // 📨 Save as a new course using existing logic
  const result = await saveCourseWithUnits(forkedCourseData);

  if (result.error) {
    return result;
  }

  // 🎬 Fork videos linked to the original course
  await forkVideosForCourse(
    originalCourse.id,
    newCourseId,
    currentUserId,
    originalCourse.owner_id // Pass the original course owner's ID
  );

  // 📁 Fork projects linked to the original course
  await forkProjectsForCourse(originalCourse.id, newCourseId, currentUserId);
  return result;
};

/**
 * Fetch all courses owned by a specific user (includes created and forked courses)
 * @param {string} userId
 */
export const fetchCoursesByUserId = async (userId) => {
  const { data: courses, error: courseError } = await supabase
    .from(courses_table_name)
    .select('*')
    .eq('owner_id', userId);

  if (courseError) {
    return { data: [], error: courseError };
  }

  const { data: units, error: unitError } = await supabase.from(units_table_name).select('*');

  if (unitError) {
    return { data: [], error: unitError };
  }

  // ✅ Normalize units and group by course_id
  const unitsByCourse = units.reduce((acc, unit) => {
    if (!acc[unit.course_id]) acc[unit.course_id] = [];
    acc[unit.course_id].push(unit);
    return acc;
  }, {});

  // ✅ Merge into each course
  const mergedCourses = courses.map((course) => ({
    ...course,
    difficultyLevel: course.difficulty_level || course.difficultyLevel || 'Beginner',
    currentLessonIndex: course.current_lesson_index ?? 0, // 👈 normalize snake_case
    progress: course.progress ?? 0, // 👈 normalize snake_case
    units: normalizeCourseUnits(unitsByCourse[course.id] || []),
  }));

  // ✅ Store merged courses in localStorage
  localStorage.setItem('courses', JSON.stringify(mergedCourses));

  return { data: mergedCourses, error: null };
};

/**
 * Update the active position (unit) for a course in Supabase
 * @param {string} courseId - ID of the course to update
 * @param {string} unitId - ID of the new active unit
 */
export const updateCoursePosition = async (courseId, nextLessonIndex) => {
  const { error } = await supabase
    .from(courses_table_name)
    .update({ current_lesson_index: nextLessonIndex }) // 🛠 Adjust to match actual column name
    .eq('id', courseId);

  if (error) {
    return { error };
  }

  return { success: true };
};

export const fetchCourseByIdWithUnits = async (courseId) => {
  // Fetch the course
  const { data: course, error: courseError } = await supabase
    .from(courses_table_name)
    .select('*')
    .eq('id', courseId)
    .single();

  if (courseError || !course) {
    return { data: null, error: courseError || new Error('Course not found') };
  }

  // Fetch its units
  const { data: units, error: unitsError } = await supabase
    .from(units_table_name)
    .select('*')
    .eq('course_id', courseId)
    .order('position', { ascending: true });

  if (unitsError) {
    return { data: null, error: unitsError };
  }

  // Normalize + merge
  const normalizedUnits = normalizeCourseUnits(units || []);

  const normalizedCourse = {
    ...course,
    difficultyLevel: course.difficulty_level || course.difficultyLevel || 'Beginner',
    currentLessonIndex: course.current_lesson_index ?? 0,
    progress: course.progress ?? 0,
    units: normalizedUnits,
  };

  // Cache to localStorage (append to "courses")
  const cached = JSON.parse(localStorage.getItem('courses') || '[]');
  const updated = [...cached.filter((c) => c.id !== courseId), normalizedCourse];
  localStorage.setItem('courses', JSON.stringify(updated));

  return { data: normalizedCourse, error: null };
};

/**
 * Fetch all courses NOT owned by the current user (for browsing/discovery)
 * @returns {Promise<Object>} - { data: Course[], error: any }
 */
export const fetchAllCoursesExceptOwned = async () => {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError || !session?.user) {
    return { data: [], error: sessionError || 'No authenticated session.' };
  }

  const currentUserId = session.user.id;

  // Fetch all courses where owner_id is NOT the current user AND fork_level = 0 (originals only)
  const { data: courses, error: courseError } = await supabase
    .from(courses_table_name)
    .select('*')
    .neq('owner_id', currentUserId)
    .eq('fork_level', 0);

  if (courseError) {
    return { data: [], error: courseError };
  }

  // Filter to keep only one course per unique creator_id
  const uniqueCreatorCourses = [];
  const seenCreators = new Set();

  for (const course of courses) {
    const creatorId = course.creator_id || course.owner_id;
    if (!seenCreators.has(creatorId)) {
      seenCreators.add(creatorId);
      uniqueCreatorCourses.push(course);
    }
  }

  // Fetch all units for these courses
  const { data: units, error: unitError } = await supabase.from(units_table_name).select('*');

  if (unitError) {
    return { data: [], error: unitError };
  }

  // Normalize units and group by course_id
  const unitsByCourse = units.reduce((acc, unit) => {
    if (!acc[unit.course_id]) acc[unit.course_id] = [];
    acc[unit.course_id].push(unit);
    return acc;
  }, {});

  // Merge units into each course
  const mergedCourses = uniqueCreatorCourses.map((course) => ({
    ...course,
    difficultyLevel: course.difficulty_level || course.difficultyLevel || 'Beginner',
    currentLessonIndex: course.current_lesson_index ?? 0,
    progress: course.progress ?? 0,
    units: normalizeCourseUnits(unitsByCourse[course.id] || []),
  }));

  return { data: mergedCourses, error: null };
};

/**
 * Fetch all Coursemates (users) taking the same course (by course ID)
 * @param {string} courseId - The course ID to find Coursemates for
 * @returns {Promise<Object>} - { data: Array of { userId, courseId, course, profile, user }, error }
 */
export const fetchClassmatesByCourseId = async (courseId) => {
  // First, get the course to find its creator_id
  const { data: targetCourse, error: courseError } = await supabase
    .from(courses_table_name)
    .select('creator_id')
    .eq('id', courseId)
    .single();

  if (courseError || !targetCourse) {
    return { data: [], error: courseError || new Error('Course not found') };
  }

  const creatorId = targetCourse.creator_id;

  // Fetch all courses with the same creator_id (all instances/forks of this course)
  const { data: courses, error: coursesError } = await supabase
    .from(courses_table_name)
    .select('*')
    .eq('creator_id', creatorId);

  if (coursesError) {
    return { data: [], error: coursesError };
  }

  // Get unique owner_ids (learners) including the creator themselves
  const learnerIds = [...new Set(courses.map((c) => c.owner_id))];

  if (learnerIds.length === 0) {
    return { data: [], error: null };
  }

  // Fetch profiles for all learners
  const { data: profiles, error: profileError } = await supabase
    .from('ml_profiles_staging')
    .select('*')
    .in('id', learnerIds);

  // eslint-disable-next-line no-console
  console.log('=== Coursemates FETCH DEBUG ===');
  // eslint-disable-next-line no-console
  console.log('Learner IDs:', learnerIds);
  // eslint-disable-next-line no-console
  console.log('Profiles fetched:', profiles);
  // eslint-disable-next-line no-console
  console.log('Profile error:', profileError);

  if (profileError) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch profiles:', profileError);
  }

  // Create maps for profiles by user ID
  const profileMap = (profiles || []).reduce((acc, profile) => {
    acc[profile.id] = profile;
    return acc;
  }, {});

  // Build learner data with course and profile information
  const learnerData = courses.map((course) => {
    const profile = profileMap[course.owner_id] || null;

    // eslint-disable-next-line no-console
    console.log(`User ${course.owner_id}:`, {
      profile,
      hasUsername: !!profile?.username,
      hasFullName: !!profile?.full_name,
      hasEmail: !!profile?.email,
    });

    return {
      userId: course.owner_id,
      courseId: course.id,
      courseName: course.title,
      forkLevel: course.fork_level,
      progress: course.progress ?? 0,
      currentLessonIndex: course.current_lesson_index ?? 0,
      profile,
      user: null, // We'll rely on profile data only
    };
  });

  // eslint-disable-next-line no-console
  console.log('Final learner data:', learnerData);
  // eslint-disable-next-line no-console
  console.log('=== END DEBUG ===');

  return { data: learnerData, error: null };
};

/**
 * Fetch all learners (users) taking courses with a specific creator_id
 * @param {string} creatorId - The creator_id to search for
 * @returns {Promise<Object>} - { data: Array of { userId, courseId, course, profile, user }, error }
 * @deprecated Use fetchClassmatesByCourseId instead for better performance
 */
export const fetchLearnersByCreatorId = async (creatorId) => {
  // Fetch all courses with the given creator_id
  const { data: courses, error: coursesError } = await supabase
    .from(courses_table_name)
    .select('*')
    .eq('creator_id', creatorId);

  if (coursesError) {
    return { data: [], error: coursesError };
  }

  // Get unique owner_ids (learners) including the creator themselves
  const learnerIds = [...new Set(courses.map((c) => c.owner_id))];

  if (learnerIds.length === 0) {
    return { data: [], error: null };
  }

  // Fetch profiles for all learners
  const { data: profiles, error: profileError } = await supabase
    .from('ml_profiles_staging')
    .select('*')
    .in('id', learnerIds);

  if (profileError) {
    return { data: [], error: profileError };
  }

  // Fetch auth user data for email/username
  const userDataPromises = learnerIds.map(async (userId) => {
    const { data, error } = await supabase.auth.admin.getUserById(userId);
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to fetch user data for ${userId}:`, error);
      return null;
    }
    return data?.user;
  });

  const usersData = await Promise.all(userDataPromises);

  // Create maps for profiles and users by user ID
  const profileMap = profiles.reduce((acc, profile) => {
    acc[profile.id] = profile;
    return acc;
  }, {});

  const userMap = usersData.reduce((acc, user) => {
    if (user) {
      acc[user.id] = user;
    }
    return acc;
  }, {});

  // Build learner data with course, profile, and user information
  const learnerData = courses.map((course) => ({
    userId: course.owner_id,
    courseId: course.id,
    courseName: course.title,
    forkLevel: course.fork_level,
    progress: course.progress ?? 0,
    currentLessonIndex: course.current_lesson_index ?? 0,
    profile: profileMap[course.owner_id] || null,
    user: userMap[course.owner_id] || null,
  }));

  return { data: learnerData, error: null };
};

/**
 * Delete a course and its associated units
 * @param {string} courseId - ID of the course to delete
 * @returns {Promise<Object>} - { success: boolean, error }
 */
export const deleteCourse = async (courseId) => {
  // First, delete all units associated with the course
  const { error: unitsError } = await supabase
    .from(units_table_name)
    .delete()
    .eq('course_id', courseId);

  if (unitsError) {
    // eslint-disable-next-line no-console
    console.error('[deleteCourse] Error deleting units:', unitsError);
    return { success: false, error: unitsError };
  }

  // Then delete the course itself
  const { error: courseError } = await supabase
    .from(courses_table_name)
    .delete()
    .eq('id', courseId);

  if (courseError) {
    // eslint-disable-next-line no-console
    console.error('[deleteCourse] Error deleting course:', courseError);
    return { success: false, error: courseError };
  }

  // Update localStorage
  const cached = JSON.parse(localStorage.getItem('courses') || '[]');
  const updated = cached.filter((c) => c.id !== courseId);
  localStorage.setItem('courses', JSON.stringify(updated));

  return { success: true, error: null };
};
